import { forwardRef, useRef, useState } from 'react';
import { Violet300 } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { type MaterialDto } from '@/api';
import { learningMaterialFileModal } from '@/components/modals/LearningMaterialModal';
import { useViewport } from '@/hooks';
import { BookFillIcon } from '@/icons';
import { UpdateFileForm } from '@/shared/components';
import { ALLOWED_EXTENSIONS, MAX_SIZE_MB, useValidateFile } from '@/shared/hooks';
import { DelayedLoadingFallback } from '@/shared/lms';
import { Checkbox } from '@/ui';
import { alertDialog, Button, DialogBody, DialogFooter, DialogHeader, ResponsiveDialog, type ResponsiveDialogProps } from '@/ui_v3';
import { DropFile } from '../../components';
import { useUploadedFile } from './useUploadedFile';
export type LearningMaterial = MaterialDto;
type Options = Pick<LearningMaterial, 'allowReopenDuringPlay'>;
export const LearningMaterialUploadModal = (props: Pick<ResponsiveDialogProps, 'onClose'> & {
  onSave: (material: LearningMaterial) => void;
  data?: LearningMaterial;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    uploadedFile,
    uploadFile,
    isUploading
  } = useUploadedFile(props.data ? {
    url: props.data.url,
    originalFileName: props.data.originalFileName,
    fileSize: props.data.fileSize
  } : null);
  const [options, setOptions] = useState<Options>({
    allowReopenDuringPlay: props.data?.allowReopenDuringPlay ?? true
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  return <ResponsiveDialog {...props} className="max-mobile-lg:h-[90%]" size="lg" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="LearningMaterialUploadModal" data-sentry-source-file="LearningMaterialUploadModal.tsx">
      <DialogHeader title={t('build:learningMaterial.button')} hasCloseButton={true} leftSlot={<div className="flex size-[36px] items-center justify-center">
            <BookFillIcon size="md" fill={Violet300} />
          </div>} onClose={props.onClose} data-sentry-element="DialogHeader" data-sentry-source-file="LearningMaterialUploadModal.tsx" />

      <DialogBody className="flex flex-col justify-between gap-lg px-lg max-mobile-lg:px-md" data-sentry-element="DialogBody" data-sentry-source-file="LearningMaterialUploadModal.tsx">
        <div className="flex flex-col gap-xs">
          <TitleAndDescription data-sentry-element="TitleAndDescription" data-sentry-source-file="LearningMaterialUploadModal.tsx" />
          <FileInputHidden ref={fileInputRef} onValidationSuccess={uploadFile} data-sentry-element="FileInputHidden" data-sentry-source-file="LearningMaterialUploadModal.tsx" />
          <div className="relative">
            {uploadedFile ? <UpdateFileForm file={{
            name: uploadedFile.originalFileName ?? ''
          }} onUpdateFile={isUploading ? undefined : triggerFileInput} withPreview={true} onClickPreview={async () => {
            logger.track('view_learningMaterial', {
              from: 'build'
            });
            await learningMaterialFileModal.openAsync({
              file: uploadedFile,
              alwaysHasCloseButton: true
            });
          }} /> : <UploadFile onValidationSuccess={uploadFile} onClickUpload={isUploading ? undefined : triggerFileInput} />}
            {isUploading && <DelayedLoadingFallback className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[calc(50%-1px)] rounded-sm border border-alternative bg-opacity-white-8 px-xl py-lg" delayMS={500} subText="LOADING" />}
          </div>
        </div>
        <Options allowReopenDuringPlay={options.allowReopenDuringPlay} onCheckAllowReopenDuringPlay={checked => {
        setOptions(prev => ({
          ...prev,
          allowReopenDuringPlay: checked
        }));
      }} data-sentry-element="Options" data-sentry-source-file="LearningMaterialUploadModal.tsx" />
      </DialogBody>

      <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="LearningMaterialUploadModal.tsx">
        <Footer onSave={() => {
        if (!uploadedFile) {
          alertDialog.open({
            header: {
              title: t('common:common.validateFile.requiredFile')
            }
          });
          return;
        }
        props.onSave({
          type: 'FILE',
          ...uploadedFile,
          allowReopenDuringPlay: options.allowReopenDuringPlay
        });
        props.onClose?.();
      }} onCancel={props.onClose} data-sentry-element="Footer" data-sentry-source-file="LearningMaterialUploadModal.tsx" />
      </DialogFooter>
    </ResponsiveDialog>;
};
const TitleAndDescription = () => {
  const {
    t
  } = useTranslation(['build']);
  return <div className="flex flex-col gap-[2px]" data-sentry-component="TitleAndDescription" data-sentry-source-file="LearningMaterialUploadModal.tsx">
      <h3 className="text-body-lg-bold">
        {t('build:learningMaterialUploadFile.title')}
      </h3>
      <p className="text-body-sm-medium text-alternative">
        {t('build:build.ai.questionFileSpec', {
        fileExt: ALLOWED_EXTENSIONS.learningMaterial.join(', '),
        fileSize: `${MAX_SIZE_MB.learningMaterial}MB`
      })}
      </p>
    </div>;
};
const FileInputHidden = forwardRef<HTMLInputElement, {
  onValidationSuccess: (file: File) => Promise<void>;
}>(({
  onValidationSuccess
}, ref) => {
  const validateFile = useValidateFile({
    service: 'learningMaterial'
  });
  return <input ref={ref} type="file" accept={ALLOWED_EXTENSIONS.learningMaterial.map(extension => `.${extension}`).join(', ')} className="hidden" onChange={async e => {
    logger.track('upload_learningMaterial_fileSelected');
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }
    logger.track('upload_learningMaterial_start');
    await onValidationSuccess(file);
    logger.track('upload_learningMaterial_end');
  }} />;
});
FileInputHidden.displayName = 'FileInputHidden';
const UploadFile = ({
  onValidationSuccess,
  onClickUpload
}: {
  onValidationSuccess: (file: File) => Promise<void>;
  onClickUpload?: () => void;
}) => {
  const validateFile = useValidateFile({
    service: 'learningMaterial'
  });
  return <DropFile onDrop={async e => {
    e.preventDefault();
    logger.track('upload_learningMaterial_fileSelected');
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!validateFile(file)) return;
    logger.track('upload_learningMaterial_start');
    await onValidationSuccess(file);
    logger.track('upload_learningMaterial_end');
  }} onClickUpload={onClickUpload} data-sentry-element="DropFile" data-sentry-component="UploadFile" data-sentry-source-file="LearningMaterialUploadModal.tsx" />;
};
const Options = (props: {
  allowReopenDuringPlay: boolean;
  onCheckAllowReopenDuringPlay: (checked: boolean) => void;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <div className="flex cursor-pointer gap-xs" onClick={() => {
    props.onCheckAllowReopenDuringPlay(!props.allowReopenDuringPlay);
  }} data-sentry-component="Options" data-sentry-source-file="LearningMaterialUploadModal.tsx">
      <Checkbox checked={props.allowReopenDuringPlay} data-sentry-element="Checkbox" data-sentry-source-file="LearningMaterialUploadModal.tsx" />
      <div className="flex flex-col gap-[0.5px]">
        <h5 className="text-body-lg-semibold">
          {t('build:learningMaterialUploadFile.openDuringPlay.title')}
        </h5>
        <p className="text-caption-lg-regular text-alternative">
          {t('build:learningMaterialUploadFile.openDuringPlay.description')}
        </p>
      </div>
    </div>;
};
const Footer = ({
  onSave,
  onCancel
}: {
  onSave: () => void;
  onCancel?: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    isMobile
  } = useViewport();
  return <div className="flex justify-end gap-xs" data-sentry-component="Footer" data-sentry-source-file="LearningMaterialUploadModal.tsx">
      <Button className="w-[132px] max-mobile-lg:w-full" variant={isMobile ? 'contained' : 'transparent'} intent="neutral" size="lg" onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="LearningMaterialUploadModal.tsx">
        {t('common.confirmDialog.cancelText')}
      </Button>
      <Button className="w-[132px] max-mobile-lg:w-full" variant="contained" intent="strong" size="lg" onClick={onSave} data-sentry-element="Button" data-sentry-source-file="LearningMaterialUploadModal.tsx">
        {t('build:learningMaterialUpload.save')}
      </Button>
    </div>;
};